import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider
} from '@mui/material';
import { Search, CheckCircle, Error, ExpandMore, Settings } from '@mui/icons-material';
import { validateUrl } from '../api';

const UrlInput = ({ onUrlValidated, initialUrl = '', initialValidationResult = null }) => {
  const [url, setUrl] = useState(initialUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationResult, setValidationResult] = useState(initialValidationResult);
  const [maxDepth, setMaxDepth] = useState(3);
  const [maxPages, setMaxPages] = useState(1000);
  const [includePatterns, setIncludePatterns] = useState('');
  const [excludePatterns, setExcludePatterns] = useState('');

  // Update local state when props change (for back navigation)
  React.useEffect(() => {
    setUrl(initialUrl);
    setValidationResult(initialValidationResult);
  }, [initialUrl, initialValidationResult]);

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleUrlChange = (event) => {
    const newUrl = event.target.value;
    setUrl(newUrl);
    setError('');
    setValidationResult(null);
  };

  const handleValidateUrl = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    if (!isValidUrl(url)) {
      setError('Please enter a valid URL (including http:// or https://)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await validateUrl(url.trim());
      setValidationResult(result);
      
      // Build URL filters object (only if patterns are specified)
      const includeList = includePatterns.split(',').map(p => p.trim()).filter(Boolean);
      const excludeList = excludePatterns.split(',').map(p => p.trim()).filter(Boolean);
      const urlFilters = (includeList.length > 0 || excludeList.length > 0) ? {
        include_patterns: includeList,
        exclude_patterns: excludeList
      } : null;
      
      // Pass the result, URL, and configuration parameters
      onUrlValidated({ 
        ...result, 
        originalUrl: url.trim(),
        maxDepth: maxDepth,
        maxPages: maxPages,
        urlFilters: urlFilters
      });
    } catch (err) {
      setError(err.message);
      setValidationResult(null);
    } finally {
      setLoading(false);
    }
  };


  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleValidateUrl();
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Enter Website URL
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Website URL"
          placeholder="https://example.com"
          value={url}
          onChange={handleUrlChange}
          onKeyPress={handleKeyPress}
          disabled={loading}
          variant="outlined"
          helperText="Enter the URL of the website you want to scrape"
        />
        
        <Button
          variant="contained"
          onClick={handleValidateUrl}
          disabled={loading || !url.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <Search />}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Checking...' : 'Validate'}
        </Button>
      </Box>

      {/* Advanced Settings */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="advanced-settings-content"
          id="advanced-settings-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings fontSize="small" />
            <Typography>Advanced Settings</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>
              Max Depth: {maxDepth}
            </Typography>
            <Slider
              value={maxDepth}
              onChange={(_, newValue) => setMaxDepth(newValue)}
              aria-labelledby="max-depth-slider"
              valueLabelDisplay="auto"
              step={1}
              marks
              min={1}
              max={10}
              disabled={loading}
            />
            <Typography variant="body2" color="text.secondary">
              How many levels deep to crawl from the starting page (1-10)
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>
              Max Pages: {maxPages}
            </Typography>
            <Slider
              value={maxPages}
              onChange={(_, newValue) => setMaxPages(newValue)}
              aria-labelledby="max-pages-slider"
              valueLabelDisplay="auto"
              step={100}
              marks={[
                { value: 100, label: '100' },
                { value: 500, label: '500' },
                { value: 1000, label: '1K' },
                { value: 2000, label: '2K' },
                { value: 5000, label: '5K' }
              ]}
              min={100}
              max={5000}
              disabled={loading}
            />
            <Typography variant="body2" color="text.secondary">
              Maximum number of pages to discover (100-5000)
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom sx={{ fontWeight: 500 }}>
              URL Filters (Optional)
            </Typography>
            <TextField
              fullWidth
              label="Include Patterns"
              placeholder="/blog, /articles, /news"
              value={includePatterns}
              onChange={(e) => setIncludePatterns(e.target.value)}
              disabled={loading}
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
              helperText="Only scrape URLs containing these patterns (comma-separated). Leave empty to include all."
            />
            <TextField
              fullWidth
              label="Exclude Patterns"
              placeholder="/tag/, /author/, /page/"
              value={excludePatterns}
              onChange={(e) => setExcludePatterns(e.target.value)}
              disabled={loading}
              variant="outlined"
              size="small"
              helperText="Skip URLs containing these patterns (comma-separated). Leave empty to exclude none."
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {validationResult && (
        <Box sx={{ mb: 2 }}>
          {validationResult.valid ? (
            <Alert 
              severity="success" 
              icon={<CheckCircle />}
              sx={{ mb: 2 }}
            >
              URL is valid and accessible!
            </Alert>
          ) : (
            <Alert 
              severity="error" 
              icon={<Error />}
              sx={{ mb: 2 }}
            >
              URL validation failed
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {validationResult.title && (
              <Chip 
                label={`Title: ${validationResult.title}`} 
                variant="outlined" 
                size="small"
              />
            )}
            {validationResult.crawl_delay && (
              <Chip 
                label={`Crawl Delay: ${validationResult.crawl_delay}s`} 
                variant="outlined" 
                size="small"
              />
            )}
            {validationResult.allowed !== undefined && (
              <Chip 
                label={validationResult.allowed ? 'Robots.txt: Allowed' : 'Robots.txt: Blocked'} 
                color={validationResult.allowed ? 'success' : 'error'}
                variant="outlined" 
                size="small"
              />
            )}
          </Box>

          {validationResult.valid && (
            <Alert severity="info" sx={{ mt: 2 }}>
              ✅ URL validated successfully! Site mapping will begin automatically with depth {maxDepth} and up to {maxPages} pages.
            </Alert>
          )}
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        <strong>Note:</strong> This scraper respects robots.txt and implements rate limiting 
        to avoid being banned. Only publicly accessible content will be scraped.
      </Typography>
    </Paper>
  );
};

export default UrlInput;
